/**
 * Хозяйство экрана = область сессии + deep-link (AK-897; AK-866 В-3: область —
 * из переключателя).
 *
 * Было (гибрид AK-772): адрес → локальный выбор на экране → память вкладки
 * (AK-699) → область панели. Каждый экран планирования держал свой контрол
 * выбора хозяйства, и одно и то же хозяйство выбиралось тремя механиками
 * (находка Н-5). Решение founder 08.10 (В-3): переключатель ОДИН — вверху
 * левого меню; локальные выборы убраны. Поэтому:
 *
 *   1. Источник — `scope.current` типа `farm` из `/scope/available`: то, что
 *      выбрано в переключателе. Своего состояния выбора у хука нет, памяти
 *      вкладки тоже — её роль играет сама область сессии.
 *   2. `?farm=` в адресе — присланная ссылка. Если хозяйство расходится с
 *      областью, хук ПЕРЕКЛЮЧАЕТ область тем же путём, что и переключатель
 *      (`useFarmDeepLink` → `useScopeSetter`, один `POST /scope/set`), и
 *      убирает `?farm=` из адреса. Хозяйство не из доступных — статус
 *      `no-access`, область не трогаем.
 *   3. В переключателе компания или кластер — вариант плана ведётся ПО
 *      ХОЗЯЙСТВУ (ADR-045 D3), «всей компании» здесь нет: статус `pick`,
 *      экран просит выбрать хозяйство в переключателе.
 *   4. Доступно ровно одно хозяйство — подставляется само (только чтение,
 *      область не пишется): выбирать нечего.
 *
 * Смена области в переключателе (событие `ikolhoz:scope-changed`) зовёт
 * `onScopeChanged` — экран сбрасывает то, что принадлежало прошлому хозяйству
 * (вариант, строку, `?farm=`). Переключение, сделанное самим deep-link'ом,
 * `onScopeChanged` НЕ зовёт: ссылка `?farm=X&variant=V` обязана открыть V.
 */
import { useMemo, useRef } from 'react';
import { useScopeChanged } from '@/lib/scopeEvent';
import type { ScopeInfo } from './ScopeTree';
import { useAvailableScopes } from './useAvailableScopes';
import { useFarmDeepLink } from './useFarmDeepLink';

export type PlanningFarmStatus =
  /** Список доступного ещё не пришёл. */
  | 'loading'
  /** Хозяйство есть — экран работает. */
  | 'ready'
  /** В переключателе компания/кластер, хозяйств несколько — выбрать в переключателе. */
  | 'pick'
  /** Ни одного доступного хозяйства. */
  | 'no-farms'
  /** Ссылка ведёт в хозяйство, которого нет среди доступных. */
  | 'no-access'
  /** Не удалось загрузить список или переключиться по ссылке. */
  | 'error';

export interface PlanningFarmScope {
  farmId: string | null;
  /** Строка `/scope/available` выбранного хозяйства (имя, гектары). */
  farm: ScopeInfo | null;
  /** Все доступные хозяйства. */
  farms: ScopeInfo[];
  status: PlanningFarmStatus;
  /** Текст ошибки для `status: 'error'`. */
  message: string | null;
  /** Повторить то, что упало (`status: 'error'`). */
  retry: () => void;
}

const NOOP = () => undefined;
const SCOPES_FAILED = 'Не удалось загрузить список хозяйств.';

export function usePlanningFarmScope({
  searchFarm,
  onScopeChanged,
  onDeepLinkApplied,
}: {
  /** `?farm=` из адреса страницы — deep-link. */
  searchFarm?: string | null;
  /**
   * Область сменили в переключателе — сбросить то, что принадлежало прошлому
   * хозяйству (и `?farm=`). Экран знает форму своего search сам.
   */
  onScopeChanged: () => void;
  /**
   * Ссылка отработала — убрать из адреса ТОЛЬКО `?farm=`. Не передан — зовётся
   * `onScopeChanged` (экраны, у которых в адресе ничего, кроме хозяйства).
   */
  onDeepLinkApplied?: () => void;
}): PlanningFarmScope {
  const scopes = useAvailableScopes();

  const changedRef = useRef(onScopeChanged);
  changedRef.current = onScopeChanged;
  const appliedRef = useRef(onDeepLinkApplied);
  appliedRef.current = onDeepLinkApplied;

  const { link, isOwnSwitch } = useFarmDeepLink(searchFarm, () =>
    (appliedRef.current ?? changedRef.current)(),
  );

  // Подписка вешается один раз (см. `useScopeChanged`), колбэки — через ref.
  useScopeChanged((detail) => {
    if (isOwnSwitch(detail)) return;
    changedRef.current();
  });

  const available = scopes.data?.available;
  const farms = useMemo(
    () => (available ?? []).filter((s) => s.scope_type === 'farm'),
    [available],
  );
  const current = scopes.data?.current;
  const sessionFarm = current?.scope_type === 'farm' ? (current.scope_id ?? null) : null;
  const onlyFarm = farms.length === 1 ? (farms[0].scope_id ?? null) : null;

  let farmId: string | null = null;
  let status: PlanningFarmStatus;
  let message: string | null = null;
  let retry: () => void = NOOP;

  if (link.state === 'switching') {
    // Хозяйство из ссылки доступно, область переключается — данные этого
    // хозяйства можно грузить сразу, не дожидаясь ответа `/scope/set`.
    farmId = link.farmId;
    status = 'ready';
  } else if (link.state === 'no-access') {
    status = 'no-access';
  } else if (link.state === 'error') {
    status = 'error';
    message = link.message;
    retry = link.retry;
  } else if (!scopes.data) {
    status = scopes.isError ? 'error' : 'loading';
    if (scopes.isError) {
      message = SCOPES_FAILED;
      retry = () => void scopes.refetch();
    }
  } else if (sessionFarm) {
    farmId = sessionFarm;
    status = 'ready';
  } else if (onlyFarm) {
    farmId = onlyFarm;
    status = 'ready';
  } else {
    status = farms.length === 0 ? 'no-farms' : 'pick';
  }

  const farm = farmId ? (farms.find((s) => s.scope_id === farmId) ?? null) : null;
  return { farmId, farm, farms, status, message, retry };
}
